import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Routes that must NOT be indexed: auth flows, user-private areas, admin, and
// cart/checkout. These are the SPA equivalents of the pages Google was flagging
// as thin/duplicate. We emit a noindex robots tag for them and still set a
// self-referencing canonical so any stray param variants collapse to one URL.
const NOINDEX_PREFIXES = [
  '/login',
  '/signin',
  '/register',
  '/create-account',
  '/registration-success',
  '/verify-email',
  '/verify-additional-email',
  '/forgot-password',
  '/reset-password',
  '/profile',
  '/my-blogs',
  '/my-services',
  '/cart',
  '/messages',
  '/admin',
  '/blog/create',
  '/blog/edit',
];

const CANONICAL_ORIGIN = 'https://unsupervisedbias.com';

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

function upsertRobots(content) {
  let el = document.querySelector('meta[name="robots"]');
  if (content == null) {
    if (el) el.parentNode.removeChild(el);
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * RouteSEO
 *
 * Runs inside <Router> and, on every navigation, sets:
 *   1. A self-referencing <link rel="canonical"> built from origin + pathname
 *      only (query string and hash stripped). This is the fix for the 153
 *      "Duplicate without user-selected canonical" pages: every route now
 *      declares its own canonical URL, so param variants
 *      (?redirect=, ?ref=, utm_*, trailing junk) all collapse to one.
 *   2. A robots noindex,follow tag on auth/admin/user-private routes so Google
 *      stops treating them as thin duplicates. Content routes get the tag
 *      removed (indexable).
 *
 * Individual content pages (BlogPost, TrendArticle) may override the canonical
 * afterwards with a more specific URL; because those run in their own effects
 * after this one, their value wins.
 */
export default function RouteSEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Normalize: strip trailing slash (except root) so "/about/" and "/about"
    // share one canonical.
    let path = pathname || '/';
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

    upsertLink('canonical', `${CANONICAL_ORIGIN}${path}`);

    const isNoindex = NOINDEX_PREFIXES.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    upsertRobots(isNoindex ? 'noindex, follow' : 'index, follow');
  }, [pathname]);

  return null;
}
